import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  loadOnlineOrderSoundEnabled,
  playOnlineOrderChime,
} from "@/lib/onlineOrderAlerts";

/**
 * Polls online-order inbox counts and plays a chime when new/pending-delivery
 * order IDs appear since the last snapshot.
 */
export function useOnlineOrderInbox({ enabled = true, intervalMs = 15000 } = {}) {
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const knownNewIds = useRef(/** @type {Set<number>|null} */ (null));
  const knownDeliveryIds = useRef(/** @type {Set<number>|null} */ (null));
  const primed = useRef(false);

  const query = useQuery({
    queryKey: ["online-orders-inbox", branchId],
    queryFn: async () => {
      const { data } = await api.get("/online-orders/inbox-summary", {
        params: { branch_id: branchId },
      });
      return data;
    },
    enabled: Boolean(enabled && branchId),
    refetchInterval: intervalMs,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    const data = query.data;
    if (!data) return;

    const newIds = new Set((data.new_orders || []).map((o) => o.id));
    const deliveryIds = new Set((data.pending_delivery || []).map((o) => o.id));

    if (!primed.current) {
      knownNewIds.current = newIds;
      knownDeliveryIds.current = deliveryIds;
      primed.current = true;
      return;
    }

    let shouldChime = false;
    for (const id of newIds) {
      if (!knownNewIds.current?.has(id)) {
        shouldChime = true;
        break;
      }
    }
    if (!shouldChime) {
      for (const id of deliveryIds) {
        if (!knownDeliveryIds.current?.has(id)) {
          shouldChime = true;
          break;
        }
      }
    }

    if (shouldChime && loadOnlineOrderSoundEnabled()) {
      playOnlineOrderChime();
    }

    knownNewIds.current = newIds;
    knownDeliveryIds.current = deliveryIds;
  }, [query.data]);

  const counts = query.data?.counts ?? {
    new: 0,
    active: 0,
    pending_delivery: 0,
    history: 0,
  };

  return {
    ...query,
    counts,
    newOrders: query.data?.new_orders ?? [],
    pendingDelivery: query.data?.pending_delivery ?? [],
    badgeTotal: (counts.new || 0) + (counts.pending_delivery || 0),
  };
}
